import {
  BaseTicketEvent,
  Ticket,
  TicketEvent,
  TicketEventType,
  TicketWithEvents,
  UUID,
} from '@certhon/models';
import { isDeepEqual } from 'remeda';

const thirtyDaysMs = 30 * 24 * 3600e3;
const twelveHoursMs = 12 * 3600e3;

export type ExtendedEventType = TicketEventType | 'TICKET_REFERENCE';

export type TicketReferencePayload = {
  ticket: Ticket;
};

export interface TicketReferenceEvent extends BaseTicketEvent {
  type: 'TICKET_REFERENCE';
  payload: TicketReferencePayload;
}

export type ExtendedTicketEvent = (TicketEvent | TicketReferenceEvent) & {
  isUserGroupStart?: true;
};

export function findFirstReferenceEvent(
  referencedTicket: TicketWithEvents,
  targetTicketId: UUID,
): TicketEvent | null {
  return (
    referencedTicket.events.find((event) =>
      event.meta?.referencedTicketIds?.includes(targetTicketId),
    ) || null
  );
}

export function createSyntheticTicketReferenceEvents(
  referencedTickets: TicketWithEvents[],
  targetTicketId: UUID,
): TicketReferenceEvent[] {
  const referenceEvents: TicketReferenceEvent[] = [];

  for (const referencedTicket of referencedTickets) {
    const event = findFirstReferenceEvent(referencedTicket, targetTicketId);

    if (event) {
      const referenceEvent: TicketReferenceEvent = {
        id: `ref-${referencedTicket.id}-${event.id}`,
        recnum: null,
        type: 'TICKET_REFERENCE',
        payload: {
          ticket: referencedTicket,
        },
        ticket_id: targetTicketId,
        user_id: event.user_id,
        created: event.created,
        inserted: event.inserted,
      };

      referenceEvents.push(referenceEvent);
    }
  }

  return referenceEvents;
}

// TODO: group by proximity for more natural grouping
export function groupEventsByUserAndDay(
  events: ExtendedTicketEvent[],
): ExtendedTicketEvent[] {
  const getKey = (event: ExtendedTicketEvent) =>
    `${event.created.toISOString().slice(0, 10)}:${event.user_id}`;

  const rv: ExtendedTicketEvent[] = [];
  let currentKey: string | null = null;
  for (const event of events) {
    const key = getKey(event);
    if (currentKey !== key) {
      rv.push({ ...event, isUserGroupStart: true });
      currentKey = key;
    } else {
      rv.push(event);
    }
  }

  return rv;
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Determine if an event is a "content" event (always visible)
 * Content events: messages, locations, attachments, ticket creation, ticket references
 */
export function isContentEvent(event: ExtendedTicketEvent): boolean {
  switch (event.type) {
    case 'ADD_MESSAGE':
    case 'ADD_LOCATION':
    case 'ADD_ATTACHMENT':
    case 'CREATE_TICKET':
    case 'TICKET_REFERENCE':
      return true;
    default:
      return false;
  }
}

/**
 * Determine if a system event should be visible based on time-based heuristics
 */
export function isRelevantEvent(
  event: ExtendedTicketEvent,
  allEventsSorted: ExtendedTicketEvent[],
  index: number,
  now: Date = new Date(),
): boolean {
  // Content events are always relevant
  if (isContentEvent(event)) {
    return true;
  }

  // Same user, same fields edited within 12 hours
  if (event.type === 'UPDATE_TICKET') {
    const precedingEvent = allEventsSorted[index - 1] ?? null;
    if (
      precedingEvent &&
      precedingEvent.type === event.type &&
      precedingEvent.user_id === event.user_id &&
      precedingEvent.created.toISOString().slice(0, 10) ===
        event.created.toISOString().slice(0, 10) &&
      isDeepEqual(
        Object.keys(event.payload).sort(),
        Object.keys(precedingEvent.payload).sort(),
      )
    ) {
      return false;
    }
  }

  const eventAge = now.getTime() - event.created.getTime();

  if (eventAge <= thirtyDaysMs) {
    return true;
  }

  // Find the most recent event of the same type
  const mostRecent = allEventsSorted.find((e) => e.type === event.type)!;

  // If this event is the most recent of its type (or part of last edit group)
  const timeDiffFromMostRecent = Math.abs(
    mostRecent.created.getTime() - event.created.getTime(),
  );

  // Show if this event is within 1 day of the most recent edit of this type
  return timeDiffFromMostRecent <= twelveHoursMs;
}

/**
 * Group consecutive hidden events together for UI collapsing
 * Returns an array that may contain either individual events or groups of hidden events
 */
export type TimelineItem =
  | { type: 'event'; event: ExtendedTicketEvent }
  | { type: 'hidden-group'; events: ExtendedTicketEvent[]; id: string };

export function groupHiddenEvents(
  sortedEvents: ExtendedTicketEvent[],
  now: Date = new Date(),
): TimelineItem[] {
  const items: TimelineItem[] = [];
  let hiddenBuffer: ExtendedTicketEvent[] = [];

  const flushHiddenBuffer = () => {
    if (hiddenBuffer.length === 0) return;

    // Only create a group if there are 2+ hidden events
    // Single hidden events are shown normally (no point in collapsing)
    if (hiddenBuffer.length === 1) {
      items.push({ type: 'event', event: hiddenBuffer[0] });
    } else {
      items.push({
        type: 'hidden-group',
        events: hiddenBuffer,
        id: `hidden-${hiddenBuffer[0].id}`,
      });
    }
    hiddenBuffer = [];
  };
  for (const [index, event] of sortedEvents.entries()) {
    const isRelevant = isRelevantEvent(event, sortedEvents, index, now);

    if (isRelevant) {
      // Flush hidden buffer before adding relevant event
      flushHiddenBuffer();
      // Add the relevant event
      items.push({ type: 'event', event });
    } else {
      // Collect hidden events
      hiddenBuffer.push(event);
    }
  }

  // Flush any remaining hidden events
  flushHiddenBuffer();

  return items;
}

/**
 * Every ticket opens with a CREATE_TICKET event — the API rejects any other
 * first event — so its absence means these events are a fragment of the
 * history rather than all of it. The sync engine stores long-closed tickets
 * without their events (see warmTickets), and offline there is no way to
 * fetch the rest.
 *
 * Not a length test: a queued message appends to the fragment, so a truncated
 * ticket can hold events and still be missing everything before them.
 */
export function isHistoryTruncated(events: TicketEvent[]): boolean {
  return !events.some((event) => event.type === 'CREATE_TICKET');
}
